'use client'

import { useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from './ui/button'
import { renderBrandLogo, type BrandLogoVariant } from './brand-logo'
import { ThemeToggle } from './utils/theme-toggle'
import { HeaderCTAButtons, HeaderNavLinks } from './data-links'
import type { HeaderCTAButton, HeaderNavLink } from './data-links'
import type { Brand } from '../lib/brand'
import { Join } from '../lib/utils'

/**
 * Single place for numeric + class defaults. `shell.*` is never replaced by
 * `classNames`; `defaults.*` is the baseline merged with optional `classNames` props.
 */
export const HeaderDefaults = {
  scroll: {
    backgroundRangePx: 100,
  },
  shell: {
    desktopNav:
      'whitespace-nowrap rounded-none px-0 py-0 text-sm leading-6 font-semibold transition-colors shrink-0',
    /** Right margin on the last text nav only (space before CTA cluster). */
    desktopNavLastTrailing: 'mr-10',
    desktopCta: 'shrink-0 whitespace-nowrap',
  },
  defaults: {
    desktopNavLinkSlot:
      'rounded-none px-0 py-0 text-sm leading-6 font-semibold transition-colors',
    desktopNavHover: 'hover:text-blue-600 dark:hover:text-blue-400',
    desktopCtaSlot: '',
    mobileLinkSlot:
      'block cursor-pointer rounded-md px-3 py-2 text-base font-medium text-nowrap transition-colors',
  },
  fallbacks: {
    navLinkActive: 'text-blue-600 dark:text-blue-400',
    navLinkInactive: 'text-slate-700 dark:text-slate-300',
    mobileLink:
      'whitespace-nowrap text-slate-700 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
  },
  mobile: {
    rowPrefix: 'whitespace-nowrap',
  },
  /** Responsive shell is fixed at the `lg` breakpoint (not configurable). */
  layout: {
    hideDesktopNav: 'lg:hidden',
    /** `lg` inset on the outer `motion.nav` only. */
    navPadLg: 'lg:pt-5 lg:px-6',
    logoFlex: 'lg:flex-1',
    /** Flex `gap-*` between CTA pills (and theme toggle), not nav text links. */
    desktopCtaClusterGapFallback: 'lg:gap-2',
    desktopNavRow: 'hidden lg:flex lg:flex-1 lg:items-center lg:justify-end',
  },
} as const

export type HeaderLogoVariant = BrandLogoVariant

/**
 * Header chrome — just two looks now:
 *
 * - `reveal`: transparent at scrollTop=0; frosted glass fades in as the user
 *             scrolls. Used by marketing heros and docs.
 * - `solid`:  always-visible slate bar. Used by /not-found, error pages, and
 *             any route where a reveal effect would look out of place.
 *
 * Two orthogonal props control behavior that used to live in named variants:
 *
 * - `themeSwitchVisible` (default `true`): show/hide the theme toggle.
 * - `forceDark`          (default `false`): add `.dark` on `<header>` when the
 *   nav must read dark but the page root is light. Prefer a parent `.dark`
 *   wrapper when the whole route is always-dark (codelogician marketing group).
 *
 * Consumers can also pass `className` to apply utilities like `font-inter`
 * to the outer `<header>` — useful when a brand's header font differs from
 * the body font.
 */
export type HeaderVariant = 'reveal' | 'solid'

/**
 * @deprecated Legacy variant strings kept compiling for one cycle. They now
 * normalize to `reveal | solid` + optional `themeSwitchVisible` hints so older
 * call sites keep sensible defaults without edits.
 *
 *  - `'marketing-glass'`    → `reveal` + `themeSwitchVisible=false` (parent should
 *                              scope `.dark` when the hero is always-dark)
 *  - `'marketing-adaptive'` → `reveal`
 *  - `'docs'`               → `reveal`
 *  - `'static'`             → `solid`
 */
export type HeaderBackgroundVariant =
  'marketing-glass' | 'marketing-adaptive' | 'docs' | 'static'

export type HeaderVariantInput = HeaderVariant | HeaderBackgroundVariant

type NormalizedHeaderVariant = {
  variant: HeaderVariant
  /** Implicit prop default set by the legacy variant name (still overridable). */
  forceDarkHint?: boolean
  themeSwitchVisibleHint?: boolean
}

function normalizeHeaderVariant(
  v: HeaderVariantInput,
): NormalizedHeaderVariant {
  switch (v) {
    case 'reveal':
    case 'solid':
      return { variant: v }
    case 'marketing-glass':
      return {
        variant: 'reveal',
        themeSwitchVisibleHint: false,
      }
    case 'marketing-adaptive':
    case 'docs':
      return { variant: 'reveal' }
    case 'static':
      return { variant: 'solid' }
  }
}

/**
 * @deprecated Slots that used to be exposed for per-site tweaks. Variant
 * defaults now cover all common cases. New code should not set these — pass
 * a `<Header brand={…} variant=… />` instead. Kept for one cycle so existing
 * call sites compile.
 */
export type HeaderClassNames = {
  desktopNavLinkSlot?: string
  desktopNavHover?: string
  desktopCtaSlot?: string
  /** @deprecated Each variant now owns its mobile panel surface. */
  mobilePanel?: string
  mobileLinkSlot?: string
}

/**
 * Internal chrome table — variants only describe surfaces/borders. Behavior
 * knobs (dark-scope, theme toggle) live on `HeaderProps` so consumers can
 * mix-and-match without inventing new variant names.
 */
type HeaderVariantConfig = {
  /** Whether the bar reveals its surface only after scroll. */
  reveal: boolean
  /** Outer `motion.nav` chrome (border, blur, etc.). */
  nav: string
  /** Static bar surface (used when `reveal` is false). */
  surface: string
  /** Reveal-bar surface (used when `reveal` is true; CSS-var driven). */
  revealSurface: string
  /** Mobile panel surface. */
  mobilePanel: string
}

const HEADER_VARIANT_CONFIG: Record<HeaderVariant, HeaderVariantConfig> = {
  reveal: {
    /** No always-on hairline — the reveal backdrop fades in on scroll and is
     *  the visual separator from the page below. Leaving a border-b here makes
     *  the bar read as a solid chrome strip even at scrollTop=0, killing the
     *  reveal effect. */
    nav: '',
    surface: '',
    revealSurface:
      'bg-(--header-glass-bg) supports-[backdrop-filter]:backdrop-blur-md',
    mobilePanel:
      'border-t border-(--header-glass-border) bg-(--header-mobile-panel-bg) shadow-lg supports-[backdrop-filter]:backdrop-blur-md',
    reveal: true,
  },
  solid: {
    nav: 'border-b border-slate-200 dark:border-slate-800',
    surface: 'backdrop-blur-md bg-slate-100 dark:bg-slate-900/95',
    revealSurface: '',
    mobilePanel:
      'border-t border-slate-200 dark:border-slate-800 bg-slate-100 dark:bg-slate-900/95 shadow-lg backdrop-blur-sm',
    reveal: false,
  },
}

function isNavItemActive(href: string, pathname: string | null): boolean {
  if (href === '/') return pathname === '/'
  return pathname === href || pathname?.startsWith(href) || false
}

/** `HeaderNavLink` vs `HeaderCTAButton` in combined lists (e.g. mobile menu). */
export function isHeaderNavLink(
  item: HeaderNavLink | HeaderCTAButton,
): item is HeaderNavLink {
  return 'colorClasses' in item
}

/**
 * Resolves `target` for header chrome. Nav treats bare http(s) as `_blank`;
 * CTA defaults to `_blank` when omitted.
 */
export function resolveHeaderLinkTarget(
  href: string,
  role: 'nav' | 'cta',
  explicit?: '_blank' | '_parent',
): '_blank' | '_parent' {
  if (explicit) return explicit
  if (role === 'cta') return '_blank'
  return href.startsWith('http') ? '_blank' : '_parent'
}

type HeaderResolvedSlots = Required<
  Pick<
    HeaderClassNames,
    | 'desktopNavLinkSlot'
    | 'desktopNavHover'
    | 'desktopCtaSlot'
    | 'mobileLinkSlot'
  >
>

/**
 * Slot precedence: explicit `classNames` prop > `brand.headerSlots` > built-in
 * default. Brand-owned slots let a site (e.g. imandrax) encode its CTA bezel
 * once on the `Brand` object instead of spreading SCREAMING_CASE constants
 * across every page that renders the header.
 */
function resolveHeaderClassNames(
  classNames?: HeaderClassNames,
  brandSlots?: Brand['headerSlots'],
): HeaderResolvedSlots {
  const d = HeaderDefaults.defaults
  return {
    desktopNavLinkSlot: classNames?.desktopNavLinkSlot ?? d.desktopNavLinkSlot,
    desktopNavHover: classNames?.desktopNavHover ?? d.desktopNavHover,
    desktopCtaSlot:
      classNames?.desktopCtaSlot ??
      brandSlots?.desktopCtaSlot ??
      d.desktopCtaSlot,
    mobileLinkSlot: classNames?.mobileLinkSlot ?? d.mobileLinkSlot,
  }
}

function useHeaderScrollBgOpacity(enabled: boolean) {
  const [opacity, setOpacity] = useState(0)

  useEffect(() => {
    if (!enabled) return
    const onScroll = () => {
      const y = window.scrollY ?? document.documentElement.scrollTop ?? 0
      setOpacity(
        Math.min(1, Math.max(0, y / HeaderDefaults.scroll.backgroundRangePx)),
      )
    }
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [enabled])

  return opacity
}

function RevealBackdrop({
  show,
  surfaceClassName,
  opacity,
}: {
  show: boolean
  surfaceClassName: string
  opacity: number
}) {
  if (!show) return null
  return (
    <div
      aria-hidden
      className={Join(
        'pointer-events-none absolute inset-0 transition-opacity duration-150 ease-in-out',
        surfaceClassName,
      )}
      style={{ opacity }}
    />
  )
}

function InnerRow({
  maxWidthClassName,
  reveal,
  children,
}: {
  maxWidthClassName: string
  reveal: boolean
  children: React.ReactNode
}) {
  return (
    <div
      className={Join(
        maxWidthClassName,
        'mx-auto flex items-center justify-between',
        reveal && 'relative z-10',
      )}
    >
      {children}
    </div>
  )
}

function LogoLink({
  logoHref,
  logoAriaLabel,
  logoNode,
}: {
  logoHref: string
  logoAriaLabel: string
  logoNode: React.ReactNode
}) {
  return (
    <Link href={logoHref} className="-m-1.5 p-1.5" aria-label={logoAriaLabel}>
      {logoNode}
    </Link>
  )
}

function MobileMenuButton({
  showThemeSwitch,
  onToggleMenu,
}: {
  showThemeSwitch: boolean
  onToggleMenu: () => void
}) {
  return (
    <div className={Join('flex', HeaderDefaults.layout.hideDesktopNav)}>
      {showThemeSwitch && <ThemeToggle />}
      <button
        type="button"
        className={Join(
          '-m-2.5 ml-2 inline-flex cursor-pointer items-center justify-center rounded-md p-2.5',
          // Same tokens as text nav: `header` can carry `.dark` (forceDark /
          // marketing) while `html` stays light — without an explicit `text-*`,
          // `currentColor` inherits body ink and the glyph reads black.
          HeaderDefaults.fallbacks.navLinkInactive,
          'transition-colors hover:text-blue-600 dark:hover:text-blue-400',
        )}
        onClick={onToggleMenu}
      >
        <span className="sr-only">Open main menu</span>
        <svg
          className="h-6 w-6 shrink-0"
          fill="none"
          viewBox="0 0 24 24"
          strokeWidth="1.5"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3.75 6.75h16.5M3.75 12h16.5m-16.5 5.25h16.5"
          />
        </svg>
      </button>
    </div>
  )
}

export type HeaderDesktopNavItemProps = {
  kind: 'nav'
  item: HeaderNavLink
  pathname: string | null
  navSlot: string
  navHoverFallback: string
  /** Last text nav gets `desktopNavLastTrailing` before the CTA cluster. */
  isLastInNavCluster?: boolean
  children: React.ReactNode
}

export type HeaderDesktopCtaItemProps = {
  kind: 'cta'
  item: HeaderCTAButton
  ctaSlot: string
  children: React.ReactNode
}

export type HeaderDesktopItemProps =
  HeaderDesktopNavItemProps | HeaderDesktopCtaItemProps

/**
 * Desktop nav text or CTA pill — same `Button` shell as `Header`, exported for custom rows.
 */
export function HeaderDesktopItem(props: HeaderDesktopItemProps) {
  if (props.kind === 'nav') {
    const {
      item,
      pathname,
      navSlot,
      navHoverFallback,
      isLastInNavCluster,
      children,
    } = props
    const isActive = isNavItemActive(item.href, pathname)
    const target = resolveHeaderLinkTarget(item.href, 'nav', item.target)
    return (
      <Button
        href={item.href}
        target={target}
        shadow={false}
        ariaLabel={item.ariaLabel}
        className={Join(
          HeaderDefaults.shell.desktopNav,
          navSlot,
          item.colorClasses?.hover || navHoverFallback,
          isActive
            ? item.colorClasses?.active ||
                HeaderDefaults.fallbacks.navLinkActive
            : item.colorClasses?.inactive ||
                HeaderDefaults.fallbacks.navLinkInactive,
          isLastInNavCluster && HeaderDefaults.shell.desktopNavLastTrailing,
        )}
      >
        {children}
      </Button>
    )
  }

  const { item, ctaSlot, children } = props
  const target = resolveHeaderLinkTarget(item.href, 'cta', item.target)
  return (
    <Button
      href={item.href}
      target={target}
      ariaLabel={item.ariaLabel}
      className={Join(
        HeaderDefaults.shell.desktopCta,
        ctaSlot,
        item.desktopClasses,
      )}
    >
      {children}
    </Button>
  )
}

export type HeaderMobileNavItemProps = {
  kind: 'nav'
  item: HeaderNavLink
  pathname: string | null
  mobileLinkSlot: string
  onNavigate: () => void
  children: React.ReactNode
}

export type HeaderMobileCtaItemProps = {
  kind: 'cta'
  item: HeaderCTAButton
  mobileLinkSlot: string
  onNavigate: () => void
  children: React.ReactNode
}

export type HeaderMobileItemProps =
  HeaderMobileNavItemProps | HeaderMobileCtaItemProps

/**
 * Mobile drawer row — same `Link` shell as `Header`, exported for custom panels.
 */
export function HeaderMobileItem(props: HeaderMobileItemProps) {
  const item = props.item
  const target = resolveHeaderLinkTarget(item.href, props.kind, item.target)
  const mobileClassName =
    props.kind === 'nav'
      ? Join(
          HeaderDefaults.mobile.rowPrefix,
          props.mobileLinkSlot,
          isNavItemActive(props.item.href, props.pathname)
            ? props.item.mobileColorClasses?.active
            : props.item.mobileColorClasses?.inactive,
        )
      : Join(
          HeaderDefaults.mobile.rowPrefix,
          props.mobileLinkSlot,
          props.item.mobileClasses,
        )

  return (
    <Link
      href={item.href}
      aria-label={item.ariaLabel}
      className={mobileClassName || HeaderDefaults.fallbacks.mobileLink}
      target={target}
      onClick={props.onNavigate}
    >
      {props.children}
    </Link>
  )
}

export type HeaderProps = {
  /**
   * Chrome shape — `reveal` (scroll-reveal glass) or `solid` (always-visible
   * slate bar). Default `solid`. Legacy strings (`marketing-glass`, `docs`,
   * `static`, `marketing-adaptive`) still work for one cycle and normalize
   * to `reveal | solid` plus implicit `themeSwitchVisible` hints where applicable
   * (`marketing-glass` hides the theme toggle only).
   */
  variant?: HeaderVariantInput
  /**
   * Site-level content (logo, nav, CTAs). Pass once per site; the same Brand
   * drives `<Footer>` too. Individual props below override fields on the brand.
   */
  brand?: Brand
  /**
   * Show the theme toggle in the header. Default `true` — most sites want
   * users to be able to switch themes. Set `false` on marketing pages whose
   * hero canvas is always-dark (codelogician, imandrax main).
   */
  themeSwitchVisible?: boolean
  /**
   * Force a `.dark` class scope on the outer `<header>` so the nav always
   * reads "dark" regardless of page theme. Default `false`. Prefer wrapping
   * the route in `.dark` when the whole subtree is always-dark; use this prop
   * when only the header should be dark-scoped (e.g. light page chrome with a
   * dark hero band).
   */
  forceDark?: boolean
  /**
   * Extra utilities applied to the outer `<header>` element. Useful for
   * brand-level font overrides — e.g. `className="font-inter"` on a site
   * whose body font isn't Inter but whose header should be.
   */
  className?: string
  logoVariant?: HeaderLogoVariant
  logoHref?: string
  logoAriaLabel?: string
  logoClassName?: string
  maxWidthClassName?: string
  navLinks?: HeaderNavLink[]
  ctaButtons?: HeaderCTAButton[]
  /** Tailwind `gap-*` for the CTA pill row only (nav links use margin instead). */
  desktopNavGapClassName?: string
  renderLogo?: (args: {
    logoVariant: HeaderLogoVariant
    className: string
  }) => React.ReactNode

  // ─── Deprecated prop surface (kept compiling for one cycle) ─────────────────
  /** @deprecated Override `--header-glass-bg` in your globals.css instead. */
  revealBackgroundClassName?: string
  /** @deprecated Use `variant`. */
  initialBackgroundClassName?: string
  /** @deprecated Use `className` on Header (cascades to nav) or a brand-level font. */
  navClassName?: string
  /** @deprecated Slot defaults are sufficient. Override `--header-glass-bg` for tinting. */
  classNames?: HeaderClassNames
}

/**
 * Site header. Pick a `variant` (`reveal | solid`) and pass a `brand` once
 * per site for content. Two orthogonal behavior knobs — `themeSwitchVisible`
 * (default `true`) and `forceDark` (default `false`) — cover the remaining
 * decisions without needing more variants.
 *
 * Tints come from CSS variables (`--header-glass-bg`, `--header-glass-border`,
 * `--header-mobile-panel-bg`) which are defined for both themes in DS globals.
 *
 * Inner row controls (`HeaderDesktopItem`, `HeaderMobileItem`,
 * `resolveHeaderLinkTarget`, `isHeaderNavLink`) are exported for custom panels.
 */
export function Header({
  variant: variantIn = 'solid',
  brand,
  themeSwitchVisible: themeSwitchVisibleIn,
  forceDark: forceDarkIn,
  className,
  logoVariant: logoVariantIn,
  logoHref: logoHrefIn,
  logoAriaLabel: logoAriaLabelIn,
  logoClassName = 'h-8 text-black dark:text-white',
  maxWidthClassName = 'max-w-8xl',
  navLinks: navLinksIn,
  ctaButtons: ctaButtonsIn,
  revealBackgroundClassName,
  initialBackgroundClassName,
  navClassName,
  classNames,
  desktopNavGapClassName,
  renderLogo,
}: HeaderProps) {
  const { variant, forceDarkHint, themeSwitchVisibleHint } =
    normalizeHeaderVariant(variantIn)
  const cfg = HEADER_VARIANT_CONFIG[variant]
  const slots = resolveHeaderClassNames(classNames, brand?.headerSlots)

  // Brand → individual prop fallback chain.
  const logoVariant: HeaderLogoVariant =
    logoVariantIn ?? brand?.logoVariant ?? 'codelogician-by-imandra'
  const logoHref = logoHrefIn ?? brand?.logoHref ?? '/'
  const logoAriaLabel =
    logoAriaLabelIn ??
    brand?.logoAriaLabel ??
    (brand ? `${brand.name} Home` : 'Site Home')
  const navLinks = navLinksIn ?? brand?.nav ?? HeaderNavLinks
  const ctaButtons = ctaButtonsIn ?? brand?.ctas ?? HeaderCTAButtons

  // Precedence: explicit prop > legacy-variant hint > built-in default.
  const themeSwitchVisible =
    themeSwitchVisibleIn ?? themeSwitchVisibleHint ?? true
  const headerForceDark = forceDarkIn ?? forceDarkHint ?? false

  const pathname = usePathname()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const reveal = cfg.reveal
  const scrollBgOpacity = useHeaderScrollBgOpacity(reveal)
  const logoNode = renderLogo
    ? renderLogo({ logoVariant, className: logoClassName })
    : renderBrandLogo({ variant: logoVariant, className: logoClassName })
  const { layout } = HeaderDefaults
  const hideDesktopNav = layout.hideDesktopNav
  const desktopCtaClusterGap =
    desktopNavGapClassName ??
    brand?.headerDesktopNavGap ??
    layout.desktopCtaClusterGapFallback
  const desktopNavRowClassName = layout.desktopNavRow

  // Surface chrome — variant config wins, deprecated props still respected
  // for one cycle so existing call sites keep rendering.
  const revealSurface = revealBackgroundClassName ?? cfg.revealSurface
  const mobilePanelClass =
    classNames?.mobilePanel ??
    brand?.headerSlots?.mobilePanel ??
    cfg.mobilePanel

  return (
    <header
      className={Join(
        'sticky top-0 z-50 w-full',
        headerForceDark && 'dark',
        className,
      )}
    >
      <div className="absolute w-full">
        <motion.nav
          data-site-header-nav
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
          className={Join(
            'mx-auto justify-between p-4',
            cfg.nav,
            layout.navPadLg,
            reveal ? 'relative' : cfg.surface,
            initialBackgroundClassName,
            navClassName,
          )}
        >
          <RevealBackdrop
            show={reveal}
            surfaceClassName={revealSurface}
            opacity={scrollBgOpacity}
          />

          <InnerRow maxWidthClassName={maxWidthClassName} reveal={reveal}>
            <div className={Join('flex', layout.logoFlex)}>
              <LogoLink
                logoHref={logoHref}
                logoAriaLabel={logoAriaLabel}
                logoNode={logoNode}
              />
            </div>

            <MobileMenuButton
              showThemeSwitch={themeSwitchVisible}
              onToggleMenu={() => setMobileMenuOpen((open) => !open)}
            />

            <div className={Join(desktopNavRowClassName, 'lg:gap-0')}>
              <div className="flex items-center">
                {navLinks.map((item, index) => (
                  <HeaderDesktopItem
                    key={item.id}
                    kind="nav"
                    item={item}
                    pathname={pathname}
                    navSlot={slots.desktopNavLinkSlot}
                    navHoverFallback={slots.desktopNavHover}
                    isLastInNavCluster={index === navLinks.length - 1}
                  >
                    {item.label}
                  </HeaderDesktopItem>
                ))}
              </div>
              <div className={Join('flex items-center', desktopCtaClusterGap)}>
                {ctaButtons.map((item) => (
                  <HeaderDesktopItem
                    key={item.id}
                    kind="cta"
                    item={item}
                    ctaSlot={slots.desktopCtaSlot}
                  >
                    {item.label}
                  </HeaderDesktopItem>
                ))}
                {themeSwitchVisible && <ThemeToggle />}
              </div>
            </div>
          </InnerRow>
        </motion.nav>

        <AnimatePresence>
          {mobileMenuOpen ? (
            <>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className={Join(
                  'fixed inset-0 z-40 bg-black/20 dark:bg-black/40',
                  hideDesktopNav,
                )}
                onClick={() => setMobileMenuOpen(false)}
              />
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className={Join(
                  'absolute top-full right-0 left-0 z-50',
                  hideDesktopNav,
                  mobilePanelClass,
                )}
              >
                <div className="space-y-1 px-4 pt-2 pb-3">
                  {[...navLinks, ...ctaButtons].map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05, duration: 0.2 }}
                    >
                      {isHeaderNavLink(item) ? (
                        <HeaderMobileItem
                          kind="nav"
                          item={item}
                          pathname={pathname}
                          mobileLinkSlot={slots.mobileLinkSlot}
                          onNavigate={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </HeaderMobileItem>
                      ) : (
                        <HeaderMobileItem
                          kind="cta"
                          item={item}
                          mobileLinkSlot={slots.mobileLinkSlot}
                          onNavigate={() => setMobileMenuOpen(false)}
                        >
                          {item.label}
                        </HeaderMobileItem>
                      )}
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            </>
          ) : null}
        </AnimatePresence>
      </div>
    </header>
  )
}
